import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, Search, SlidersHorizontal } from 'lucide-react';
import './DecisionInterne.css';

/* ────────────────────────── Types ────────────────────────── */

type StatutDecision = 'importe' | 'en-traitement' | 'anonymise' | 'controle' | 'valide';

interface Partie {
  nom: string;
  qualite: string;
  avocat?: string;
}

interface Decision {
  id: string;
  numero: string;
  numeroRG: string;
  date: string;
  juridiction: string;
  matiere: string;
  typeDecision: string;
  objet: string;
  synthese: string;
  statut: StatutDecision;
  publiee: boolean;
  parties: Partie[];
}

/* ────────────────────────── Labels ────────────────────────── */

const LABELS_STATUT: Record<StatutDecision, string> = {
  importe: 'Importé',
  'en-traitement': 'En traitement',
  anonymise: 'Anonymisé',
  controle: 'Contrôlé',
  valide: 'Validé',
};

/* ────────────────────────── Données mock ────────────────────────── */

const DECISIONS: Decision[] = [
  {
    id: 'dec-1', numero: 'Jugement 1187/2024', numeroRG: '2024/1187',
    date: '2024-11-14', juridiction: "Tribunal de commerce d'Abidjan",
    matiere: 'Sûretés et garanties', typeDecision: 'Jugement',
    objet: "Appel en garantie sur caution bancaire solidaire",
    synthese: "Le tribunal retient l'engagement de caution solidaire souscrit par la banque et la condamne au paiement du principal réclamé, avec exécution provisoire partielle.",
    statut: 'controle', publiee: false,
    parties: [
      { nom: 'SOCIÉTÉ IVOMAT SARL', qualite: 'Demanderesse', avocat: 'Me BAMBA Salif' },
      { nom: "BANQUE ATLANTIQUE CÔTE D'IVOIRE", qualite: 'Défenderesse', avocat: 'Cabinet DIALLO & Associés' },
    ],
  },
  {
    id: 'dec-2', numero: 'Jugement 921/2025', numeroRG: '2025/0921',
    date: '2025-07-23', juridiction: "Tribunal de commerce d'Abidjan",
    matiere: 'Droit des sociétés', typeDecision: 'Jugement',
    objet: "Litige relatif à la qualité des parties et immatriculation",
    synthese: "La demanderesse conteste l'immatriculation de la défenderesse au RCCM et sollicite sa radiation pour activité non conforme à l'objet social déclaré.",
    statut: 'anonymise', publiee: false,
    parties: [
      { nom: 'SISSOKO Aïcha Modibo Kane', qualite: 'Demanderesse' },
      { nom: 'COURS SECONDAIRE MARIE HOULDA SARL', qualite: 'Défenderesse' },
    ],
  },
  {
    id: 'dec-3', numero: 'Jugement 761/2025', numeroRG: '2025/0761',
    date: '2025-04-15', juridiction: "Tribunal de commerce d'Abidjan",
    matiere: 'Recouvrement', typeDecision: 'Jugement',
    objet: "Instance en paiement de factures et intérêts de retard",
    synthese: "Le tribunal condamne la partie défenderesse au paiement des factures impayées, majorées des intérêts de droit à compter de la mise en demeure.",
    statut: 'valide', publiee: true,
    parties: [
      { nom: 'SOCIÉTÉ KANZA DISTRIBUTION', qualite: 'Demanderesse', avocat: 'Me OUATTARA' },
      { nom: 'ETS KOUADIO FRÈRES', qualite: 'Défenderesse' },
    ],
  },
  {
    id: 'dec-4', numero: 'Ordonnance 421/2025', numeroRG: '2025/0421',
    date: '2025-06-04', juridiction: "Tribunal de commerce d'Abidjan",
    matiere: 'Voies d\u2019exécution', typeDecision: 'Ordonnance',
    objet: "Saisie attribution de créances sur compte bancaire",
    synthese: "Le juge autorise la saisie attribution sur les comptes bancaires du débiteur pour le recouvrement de la créance judiciairement constatée.",
    statut: 'importe', publiee: false,
    parties: [
      { nom: 'SOCIÉTÉ AGRO CI', qualite: 'Demanderesse', avocat: 'Me KONÉ Ibrahim' },
      { nom: 'M. KOFFI N\u2019DRI', qualite: 'Défendeur' },
    ],
  },
  {
    id: 'dec-5', numero: 'Jugement 819/2025', numeroRG: '2025/0819',
    date: '2025-10-18', juridiction: "Tribunal de commerce d'Abidjan",
    matiere: 'Baux commerciaux', typeDecision: 'Jugement',
    objet: "Renouvellement du bail commercial et indemnité d'éviction",
    synthese: "Le refus de renouvellement ouvre droit à une indemnité d'éviction calculée selon la valeur du fonds exploité dans les locaux.",
    statut: 'en-traitement', publiee: false,
    parties: [
      { nom: 'ETS N\u2019DA SERVICES', qualite: 'Demandeur' },
      { nom: 'IMMOBILIÈRE LAGUNE', qualite: 'Défenderesse', avocat: 'Cabinet SORO & Partners' },
    ],
  },
  {
    id: 'dec-6', numero: 'Jugement 3052/2023', numeroRG: '2023/3052',
    date: '2023-12-07', juridiction: "Tribunal de commerce d'Abidjan",
    matiere: 'Bail commercial', typeDecision: 'Jugement',
    objet: "Résiliation de bail pour défaut de paiement des loyers",
    synthese: "Le tribunal prononce la résiliation du bail commercial aux torts du preneur, condamné au paiement des arriérés de loyers et à la libération des lieux.",
    statut: 'valide', publiee: true,
    parties: [
      { nom: 'SCI PLATEAU IMMOBILIER', qualite: 'Demanderesse' },
      { nom: 'RESTAURANT LE JARDIN SARL', qualite: 'Défenderesse', avocat: 'Me TOURÉ Amadou' },
    ],
  },
];

/* ────────────────────────── Recherche ────────────────────────── */

interface Criteres {
  phrase: string;
  matiere: string;
  type: string;
  dateDebut: string;
  dateFin: string;
  dateExacte: string;
  numeroRG: string;
  numeroDecision: string;
  demandeur: string;
  defendeur: string;
}

const CRITERES_VIDES: Criteres = {
  phrase: '', matiere: '', type: '', dateDebut: '', dateFin: '',
  dateExacte: '', numeroRG: '', numeroDecision: '', demandeur: '', defendeur: '',
};

const MATIERES = Array.from(new Set(DECISIONS.map(d => d.matiere))).sort();
const TYPES = Array.from(new Set(DECISIONS.map(d => d.typeDecision))).sort();

function normaliser(texte: string): string {
  return texte.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function filtrer(c: Criteres, decisions: Decision[]): Decision[] {
  const mots = c.phrase.trim() ? c.phrase.trim().split(/\s+/) : [];
  const rg = normaliser(c.numeroRG.trim());
  const num = normaliser(c.numeroDecision.trim());
  const dem = normaliser(c.demandeur.trim());
  const def = normaliser(c.defendeur.trim());

  return decisions.filter(d => {
    if (c.matiere && d.matiere !== c.matiere) return false;
    if (c.type && d.typeDecision !== c.type) return false;
    if (c.dateDebut && d.date < c.dateDebut) return false;
    if (c.dateFin && d.date > c.dateFin) return false;
    if (c.dateExacte && d.date !== c.dateExacte) return false;
    if (rg && !normaliser(d.numeroRG).includes(rg)) return false;
    if (num && !normaliser(d.numero).includes(num)) return false;
    if (dem && !d.parties.some(p => ['demanderesse', 'demandeur'].includes(normaliser(p.qualite)) && normaliser(p.nom).includes(dem))) return false;
    if (def && !d.parties.some(p => ['défenderesse', 'défendeur'].includes(normaliser(p.qualite)) && normaliser(p.nom).includes(def))) return false;
    if (mots.length === 0) return true;
    const bloc = normaliser([d.numeroRG, d.numero, d.juridiction, d.matiere, d.objet, d.synthese, ...d.parties.map(p => p.nom)].join(' '));
    return mots.every(m => bloc.includes(normaliser(m)));
  });
}

function formatDateFR(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/* ────────────────────────── Composant principal ────────────────────────── */

const DecisionInterne: React.FC = () => {
  const [criteres, setCriteres] = useState<Criteres>(CRITERES_VIDES);
  const [recherche, setRecherche] = useState<Criteres | null>(null);
  const [avancee, setAvancee] = useState(false);
  const navigate = useNavigate();

  const resultats = useMemo(
    () => recherche ? filtrer(recherche, DECISIONS) : [],
    [recherche],
  );

  const maj = (cle: keyof Criteres) => (val: string) =>
    setCriteres(c => ({ ...c, [cle]: val }));

  const lancer = () => setRecherche(criteres);
  const reinitialiser = () => { setCriteres(CRITERES_VIDES); setRecherche(null); };

  return (
    <main className="di-page">
      {/* Hero */}
      <section className="di-hero">
        <div className="di-hero-gradient" />
        <div className="di-hero-content">
          <p className="di-eyebrow">Datagreffe Justice</p>
          <h1>Retrouvez l'ensemble de vos décisions</h1>
          <p className="di-description">
            Toute la jurisprudence de votre juridiction en un seul endroit :
            recherchez en une phrase, par numéro, par partie ou par matière.
          </p>
          <p className="di-perimetre">
            <strong>Jurisprudence interne</strong> — Décisions de votre juridiction, avec les noms des parties, accessibles uniquement en interne.
          </p>

          <form className="di-search-form" onSubmit={e => { e.preventDefault(); lancer(); }}>
            <div className="di-search-input">
              <Search size={22} />
              <input
                value={criteres.phrase}
                onChange={e => maj('phrase')(e.target.value)}
                placeholder="Recherchez une décision de justice ou un texte de loi\u2026"
              />
            </div>
            <button type="submit" className="di-search-btn">Rechercher</button>
          </form>

          <button className="di-advanced-toggle" onClick={() => setAvancee(v => !v)}>
            <SlidersHorizontal size={16} /> Recherche avancée
          </button>
        </div>
      </section>

      {/* Filtres avancés */}
      {avancee && (
        <div className="di-advanced">
          <div className="di-advanced-grid">
            <div>
              <label className="di-field-label">Période — du</label>
              <input type="date" value={criteres.dateDebut} onChange={e => maj('dateDebut')(e.target.value)} className="di-field" />
            </div>
            <div>
              <label className="di-field-label">Période — au</label>
              <input type="date" value={criteres.dateFin} onChange={e => maj('dateFin')(e.target.value)} className="di-field" />
            </div>
            <div>
              <label className="di-field-label">Date de la décision</label>
              <input type="date" value={criteres.dateExacte} onChange={e => maj('dateExacte')(e.target.value)} className="di-field" />
            </div>
            <div>
              <label className="di-field-label">Numéro RG</label>
              <input value={criteres.numeroRG} onChange={e => maj('numeroRG')(e.target.value)} placeholder="Ex. : 2024/1187" className="di-field" />
            </div>
            <div>
              <label className="di-field-label">Numéro de décision</label>
              <input value={criteres.numeroDecision} onChange={e => maj('numeroDecision')(e.target.value)} placeholder="Ex. : 1187/2024" className="di-field" />
            </div>
            <div>
              <label className="di-field-label">Matière</label>
              <select value={criteres.matiere} onChange={e => maj('matiere')(e.target.value)} className="di-field">
                <option value="">Toutes les matières</option>
                {MATIERES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="di-field-label">Demandeur</label>
              <input value={criteres.demandeur} onChange={e => maj('demandeur')(e.target.value)} placeholder="Ex. : IVOMAT" className="di-field" />
            </div>
            <div>
              <label className="di-field-label">Défendeur</label>
              <input value={criteres.defendeur} onChange={e => maj('defendeur')(e.target.value)} placeholder="Ex. : BANQUE ATLANTIQUE" className="di-field" />
            </div>
            <div>
              <label className="di-field-label">Type de décision</label>
              <select value={criteres.type} onChange={e => maj('type')(e.target.value)} className="di-field">
                <option value="">Tous les types</option>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="di-advanced-actions">
            <button className="di-reset-btn" onClick={reinitialiser}>Réinitialiser</button>
            <button className="di-apply-btn" onClick={lancer}>Lancer la recherche</button>
          </div>
        </div>
      )}

      {/* État vide */}
      {!recherche && (
        <p className="di-empty">Lancez une recherche pour afficher les décisions enregistrées par la juridiction.</p>
      )}

      {/* Résultats */}
      {recherche && (
        <section className="di-results">
          <p className="di-results-count">
            {resultats.length} décision{resultats.length > 1 ? 's' : ''} trouvée{resultats.length > 1 ? 's' : ''} — Jurisprudence interne
          </p>

          {resultats.length === 0 && (
            <div className="di-no-results">
              <FileText size={28} />
              <strong>Aucune décision ne correspond à cette recherche.</strong>
            </div>
          )}

          <div className="di-results-list">
            {resultats.map(d => (
              <article key={d.id} className="di-result-card">
                <div className="di-result-heading">
                  <span className="di-doc-icon"><FileText size={18} /></span>
                  <div className="di-result-title">
                    <h3>Décision {d.numero.replace(/^(Jugement|Arrêt|Ordonnance)\s*/i, '')} · {formatDateFR(d.date)}</h3>
                    <p>{d.juridiction} · {d.numeroRG} · {d.matiere} · Première instance</p>
                  </div>
                  <div className="di-result-badges">
                    <span className={`di-badge-statut di-statut-${d.statut}`}>{LABELS_STATUT[d.statut]}</span>
                    <span className={`di-badge-pub ${d.publiee ? 'di-pub-oui' : ''}`}>
                      {d.publiee ? 'Publiée' : 'Non publiée'}
                    </span>
                  </div>
                </div>

                <p className="di-result-objet">{d.objet}</p>
                <p className="di-result-synthese">{d.synthese}</p>

                <div className="di-result-footer">
                  <span className="di-result-parties">{d.parties.map(p => p.nom).join(' · ')}</span>
                  <button className="di-voir-btn" onClick={() => navigate(`/espace-tca/decisions-internes/${d.id}`)}>
                    Voir la décision <ArrowRight size={16} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

    </main>
  );
};

export default DecisionInterne;
